import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthLayout from "../../shared/ui/AuthLayout";
import AuthSectionHeader from "../../shared/ui/AuthSectionHeader";
import Button from "../../shared/ui/Button";
import Input from "../../shared/ui/Input";
import { getPostLoginPath } from "../../lib/authRoutes";
import { persistAuthUser, clearImpersonationState } from "../../lib/authSession";
import { applyAccountStatusToSession } from "../../lib/accountStatus";
import { verifyLoginCode, resendLoginCode } from "../../lib/twoFactorApi";
import { useTranslation, resolveInitialLocale } from "../../i18n";
import api from "../../utils/api";

/**
 * Mask an email for the 2FA screen: keep the first 2 and last 2 characters of
 * the local part and the full domain, dots in between.
 * e.g. "moeenkhanmoeenkhan6@gmail.com" -> "mo••••n6@gmail.com"
 */
function maskEmail(email) {
  const value = (email || "").trim();
  if (!value.includes("@")) return value;
  const [local, domain] = value.split("@");
  if (local.length <= 2) {
    return `${local.charAt(0) || ""}••@${domain}`;
  }
  const start = local.slice(0, 2);
  const end = local.length > 3 ? local.slice(-2) : local.slice(-1);
  return `${start}••••${end}@${domain}`;
}

const EyeIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2 12C3.8 8.5 7.4 6 12 6C16.6 6 20.2 8.5 22 12C20.2 15.5 16.6 18 12 18C7.4 18 3.8 15.5 2 12Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const LoginForm = () => {
  const navigate = useNavigate();
  const { t, setLocale } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const timeoutRef = useRef(null);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });

  const [errors, setErrors] = useState({});

  // Two-factor step state: when a challenge is set, the code step is shown.
  const [twoFactorChallenge, setTwoFactorChallenge] = useState(null);
  const [twoFactorDevMode, setTwoFactorDevMode] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = window.setInterval(
      () => setResendCooldown((s) => (s > 0 ? s - 1 : 0)),
      1000
    );
    return () => window.clearInterval(timer);
  }, [resendCooldown > 0]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = t("auth.emailRequired");
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t("auth.emailInvalid");
    }

    if (!formData.password.trim()) {
      newErrors.password = t("auth.passwordRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const completeLogin = (data) => {
    const accessToken = data?.access;
    if (accessToken) {
      window.localStorage.setItem("repman_access_token", accessToken);
    }

    const companyId = data?.user?.company?.id ?? data?.user?.company_id;
    if (companyId) {
      window.localStorage.setItem("repman_company_id", String(companyId));
    }

    if (data?.user) {
      clearImpersonationState();
      persistAuthUser(data.user, { allowUserSwitch: true });
      applyAccountStatusToSession(data.user);
    }
    setLocale(resolveInitialLocale());
    const role = data?.user?.role || "user";
    navigate(getPostLoginPath(role));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);
      setErrors((prev) => ({ ...prev, form: "" }));
      try {
        const data = await api.post("/api/users/login/", {
          email: formData.email.trim(),
          password: formData.password,
        });

        if (data?.two_factor_required && data?.challenge) {
          setTwoFactorChallenge(data.challenge);
          setTwoFactorDevMode(Boolean(data.dev_mode));
          setOtpCode("");
          setResendCooldown(60);
          return;
        }

        completeLogin(data);
      } catch (err) {
        setErrors((prev) => ({
          ...prev,
          form:
            err?.data?.detail ||
            t("auth.loginFailed"),
        }));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!otpCode.trim() || isSubmitting) return;
    setIsSubmitting(true);
    setErrors((prev) => ({ ...prev, form: "" }));
    try {
      const data = await verifyLoginCode(twoFactorChallenge, otpCode.trim());
      completeLogin(data);
    } catch (err) {
      if (err?.data?.code === "challenge_expired") {
        setTwoFactorChallenge(null);
        setOtpCode("");
        setErrors((prev) => ({ ...prev, form: t("auth.twoFactorExpired") }));
      } else {
        setErrors((prev) => ({
          ...prev,
          form: t("auth.twoFactorInvalidCode"),
        }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    setErrors((prev) => ({ ...prev, form: "" }));
    try {
      await resendLoginCode(twoFactorChallenge);
      setResendCooldown(60);
    } catch (err) {
      if (err?.data?.code === "cooldown") {
        setResendCooldown(err?.data?.retry_in || 60);
      } else if (err?.data?.code === "challenge_expired") {
        setTwoFactorChallenge(null);
        setErrors((prev) => ({ ...prev, form: t("auth.twoFactorExpired") }));
      }
    }
  };

  const handleBackToLogin = () => {
    setTwoFactorChallenge(null);
    setOtpCode("");
    setErrors({});
  };

  useEffect(() => {
    return () => {
      setIsSubmitting(false);
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, []);

  const InlineSpinner = () => (
    <svg
      className="h-4 w-4 animate-spin text-white"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="rgba(255,255,255,0.35)"
        strokeWidth="3"
      />
      <path
        d="M22 12a10 10 0 0 0-10-10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );

  if (twoFactorChallenge) {
    return (
      <AuthLayout consentText="Mit der Anmeldung stimmen Sie unseren" showBenefits>
        <form onSubmit={handleVerifyCode}>
          <AuthSectionHeader
            title={t("auth.twoFactorTitle")}
            description={t("auth.twoFactorSubtitle", {
              email: maskEmail(formData.email),
            })}
          />

          {twoFactorDevMode ? (
            <p className="mb-4 rounded-lg bg-yellow-500/10 px-3 py-2 text-[13px] text-yellow-800">
              {t("auth.twoFactorDevMode")}
            </p>
          ) : null}

          <div className="space-y-6">
            <div>
              <label
                htmlFor="otpCode"
                className="mb-3 block text-[12px] font-bold uppercase tracking-[0.02em] text-[#111111]"
              >
                {t("auth.twoFactorCodeLabel")}
              </label>
              <input
                id="otpCode"
                name="otpCode"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={otpCode}
                onChange={(e) =>
                  setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="••••••"
                autoFocus
                className="h-14 w-full rounded-[12px] border border-transparent bg-[#dfe3e6] text-center text-[26px] font-semibold tracking-[0.5em] text-[#111111] outline-none transition placeholder:tracking-[0.4em] placeholder:text-[#9ca3af] focus:border-black"
              />
            </div>

            <Button
              type="submit"
              showArrow={!isSubmitting}
              className="mt-2"
              disabled={isSubmitting || otpCode.length !== 6}
            >
              {isSubmitting ? (
                <span className="inline-flex items-center gap-2">
                  <InlineSpinner />
                  <span>{t("auth.twoFactorVerifying")}</span>
                </span>
              ) : (
                t("auth.twoFactorVerify")
              )}
            </Button>

            {errors.form && (
              <p className="text-[13px] text-red-600" role="alert">
                {errors.form}
              </p>
            )}

            <div className="flex items-center justify-between text-[13px]">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={resendCooldown > 0}
                className="font-semibold text-[#111111] hover:underline disabled:cursor-default disabled:text-[#9ca3af] disabled:no-underline"
              >
                {resendCooldown > 0
                  ? t("auth.twoFactorResendIn", { seconds: resendCooldown })
                  : t("auth.twoFactorResend")}
              </button>
              <button
                type="button"
                onClick={handleBackToLogin}
                className="text-[#4b5563] transition hover:text-black"
              >
                {t("auth.twoFactorBack")}
              </button>
            </div>
          </div>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout consentText="Mit der Anmeldung stimmen Sie unseren" showBenefits>
      <form onSubmit={handleSubmit}>
        <AuthSectionHeader
          title={t("auth.loginTitle")}
          description={t("auth.loginSubtitle")}
        />

        <div className="space-y-6">
          <Input
            type="email"
            name="email"
            label={t("auth.email")}
            value={formData.email}
            onChange={handleChange}
            placeholder="max@unternehmen.de"
            error={errors.email}
          />

          <Input
            type={showPassword ? "text" : "password"}
            name="password"
            label={t("auth.password")}
            value={formData.password}
            onChange={handleChange}
            placeholder="••••••••••••"
            error={errors.password}
            action={
              <Link
                to="/passwort-vergessen"
                className="text-[12px] font-semibold text-[#4b5563] transition hover:text-black"
              >
                {t("auth.forgotPassword")}
              </Link>
            }
            rightElement={
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="text-[#4b5563] transition hover:text-black"
                aria-label={showPassword ? "Passwort ausblenden" : "Passwort anzeigen"}
              >
                <EyeIcon />
              </button>
            }
          />

          <div className="flex items-center justify-between gap-4">
            <label className="flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={handleChange}
                className="h-4 w-4 rounded border-[#cdd3d9] text-black focus:ring-black"
              />
              <span className="text-[13px] text-[#4b5563]">{t("auth.rememberMe")}</span>
            </label>
          </div>

          <Button
            type="submit"
            showArrow={!isSubmitting}
            className="mt-2"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="inline-flex items-center gap-2">
                <InlineSpinner />
                <span>{t("auth.signingIn")}</span>
              </span>
            ) : (
              t("auth.login")
            )}
          </Button>
          {errors.form && (
            <p className="text-[13px] text-red-600" role="alert">
              {errors.form}
            </p>
          )}
        </div>

        <div className="my-8 h-px w-full bg-[#ececec]" />

        <p className="text-center text-[14px] text-[#4b5563]">
          {t("auth.noAccount")}{" "}
          <Link to="/" className="font-semibold text-[#111111] hover:underline">
            {t("auth.signUp")}
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
};

export default LoginForm;
